import {
  FcArea,
  FcColor,
  FcElement,
  FcErrorCorrectionLevel,
  FcGradient,
  FcPercentageSize,
  FcPositionProperty,
  FcShape
} from './fc_common.types'
import { FcModule, FcPipeModuleType, FcPipeType } from './fc_module.types'
import { FcPipes, FcRandomShapes } from './fc_generator.types'
import rand from 'random-seed'

const pipeMap = new Map<number, FcPipeModuleType>([
  [0b0000, { type: FcPipeType.SINGLE, rotate: 0 }],
  [0b1100, { type: FcPipeType.ELBOW, rotate: 0 }],
  [0b0110, { type: FcPipeType.ELBOW, rotate: 90 }],
  [0b0011, { type: FcPipeType.ELBOW, rotate: 180 }],
  [0b1001, { type: FcPipeType.ELBOW, rotate: 270 }],
  [0b1010, { type: FcPipeType.COUPLE, rotate: 0 }],
  [0b0101, { type: FcPipeType.COUPLE, rotate: 90 }],
  [0b1111, { type: FcPipeType.CROSS, rotate: 0 }],
  [0b1101, { type: FcPipeType.TEE, rotate: 270 }],
  [0b1110, { type: FcPipeType.TEE, rotate: 0 }],
  [0b0111, { type: FcPipeType.TEE, rotate: 90 }],
  [0b1011, { type: FcPipeType.TEE, rotate: 180 }],
  [0b1000, { type: FcPipeType.CAP, rotate: 180 }],
  [0b0100, { type: FcPipeType.CAP, rotate: 270 }],
  [0b0010, { type: FcPipeType.CAP, rotate: 0 }],
  [0b0001, { type: FcPipeType.CAP, rotate: 90 }]
])

export interface ErasedData {
  percentErased: number
  overLimit: boolean
  numErasedInBlock: number
  limitErasedInBlock: number
}

export interface RsGroup {
  rsnum: number
  numBytes: number
  numBadBytes: number
  allowedErrors: number
  bytes: Set<number>
  badBytes: Set<number>
}

export interface FcModuleImageInfo {
  urls: string[]
}

function splitModuleUrls(urls?: string | null): FcModuleImageInfo {
  if (!urls) {
    return { urls: [] }
  }
  if (urls.indexOf(',') !== -1) {
    const splitUrls = urls.split(',')
    return { urls: splitUrls }
  } else {
    return { urls: [urls] }
  }
}

export class FcGridProps {
  padModuleShape?: FcShape | null
  baseModuleShape?: FcShape | null
  gradient?: FcGradient
  randomSizes: FcPercentageSize[] = []
  numRandomSizes = 0
  moduleImageInfo: FcModuleImageInfo
  colors: FcColor[] = []
  numColors = 0
  padModuleColors: FcColor[] = []
  numPadModuleColors = 0
  fillSvg: boolean
  backgroundColor: FcColor
  outer: boolean
  pipeImageUrls?: Map<FcPipeType, FcModuleImageInfo>
  randomShapes?: FcRandomShapes
  seedGenerator: rand.RandomSeed

  constructor(
    outer: boolean,
    baseModuleColor: FcColor,
    backgroundColor: FcColor,
    baseModuleShape?: FcShape | null,
    gradient?: FcGradient,
    moduleImageUrl?: string | null,
    randomSizes?: string,
    fillSvg?: boolean,
    pipes?: FcPipes,
    randomShapes?: FcRandomShapes,
    padModuleShape?: FcShape,
    padModuleColor?: FcColor,
    seed?: string
  ) {
    this.baseModuleShape = baseModuleShape
    this.padModuleShape = padModuleShape || baseModuleShape

    this.randomShapes = randomShapes
    this.gradient = gradient
    this.outer = outer
    this.fillSvg = fillSvg || false
    this.backgroundColor = backgroundColor
    this.seedGenerator = rand.create(seed)

    if (pipes) {
      this.pipeImageUrls = new Map<FcPipeType, FcModuleImageInfo>()
      this.pipeImageUrls.set(FcPipeType.CAP, splitModuleUrls(pipes.cap))
      this.pipeImageUrls.set(FcPipeType.SINGLE, splitModuleUrls(pipes.single))
      this.pipeImageUrls.set(FcPipeType.TEE, splitModuleUrls(pipes.tee))
      this.pipeImageUrls.set(FcPipeType.CROSS, splitModuleUrls(pipes.cross))
      this.pipeImageUrls.set(FcPipeType.COUPLE, splitModuleUrls(pipes.couple))
      this.pipeImageUrls.set(FcPipeType.ELBOW, splitModuleUrls(pipes.elbow))
    }
    this.moduleImageInfo = splitModuleUrls(moduleImageUrl)
    if (baseModuleColor.indexOf(',') !== -1) {
      this.colors = baseModuleColor.split(',')
      this.numColors = this.colors.length
    } else {
      this.colors = [baseModuleColor]
      this.numColors = 1
    }
    if (padModuleColor) {
      if (padModuleColor.indexOf(',') !== -1) {
        this.padModuleColors = padModuleColor.split(',')
        this.numPadModuleColors = this.padModuleColors.length
      } else {
        this.padModuleColors = [padModuleColor]
        this.numPadModuleColors = 1
      }
    }
    if (!gradient && randomSizes) {
      const strs = randomSizes.split(',')
      for (let i = 0; i < strs.length; i++) {
        this.randomSizes[i] = parseInt(strs[i], 10) / 100.0
      }
      this.numRandomSizes = this.randomSizes.length
    }
  }

  getColor(isPadModule?: boolean): string {
    if (this.numPadModuleColors && isPadModule) {
      return this.numPadModuleColors === 1
        ? this.padModuleColors[0]
        : this.padModuleColors[this.seedGenerator(this.numPadModuleColors)]
    }
    return this.numColors === 1 ? this.colors[0] : this.colors[this.seedGenerator(this.numColors)]
  }
}

export class FcGrid {
  modules: FcModule[][] = []
  dataModuleCount = 0
  totalModuleCount = 0
  dataModuleOffset = 0
  dataModuleBoundary = 0
  xsize = 0
  ysize = 0
  errorCorrectionLevel: FcErrorCorrectionLevel = FcErrorCorrectionLevel.M
  typeNumber = 0
  overrideTypeNumber?: number
  positionElementAreas = new Map<FcPositionProperty, FcArea>()
  dataProps: FcGridProps
  outerProps: FcGridProps
  dataGridPercentage: FcPercentageSize
  emptySpaceSize: number
  doContainerPattern: boolean
  seedGenerator: rand.RandomSeed
  radiusMask?: FcPercentageSize | null
  highByte = 0
  rotate?: boolean
  erasedExact?: boolean
  erasedExactTolerance?: number
  calcBlendErasurePad?: number
  data: string

  constructor(
    dataGridPercentage: FcPercentageSize,
    dataProps: FcGridProps,
    outerProps: FcGridProps,
    errorCorrectionLevel: FcErrorCorrectionLevel,
    emptySpaceSize: number,
    doContainerPattern: boolean,
    seed: string,
    radiusMask?: FcPercentageSize | null,
    overrideTypeNumber?: number,
    rotate?: boolean,
    erasedExact?: boolean,
    erasedExactTolerance?: number,
    calcBlendErasurePad?: number,
    data?: string
  ) {
    this.dataProps = dataProps
    this.outerProps = outerProps
    this.errorCorrectionLevel = errorCorrectionLevel
    // We subtract 5 so calculation is closer to old one for compatibility
    this.dataGridPercentage = dataGridPercentage === 100 ? 100 : dataGridPercentage - 5
    this.emptySpaceSize = emptySpaceSize
    this.doContainerPattern = doContainerPattern
    const ind = seed.indexOf('#')
    if (ind !== -1) {
      seed = seed.substr(0, ind)
    }
    this.data = data || ''
    this.seedGenerator = rand.create(seed)
    this.radiusMask = radiusMask
    this.overrideTypeNumber = overrideTypeNumber
    this.rotate = rotate
    this.erasedExact = erasedExact
    this.erasedExactTolerance = erasedExactTolerance
    this.calcBlendErasurePad = calcBlendErasurePad
  }

  getProps(row: number, col: number): FcGridProps {
    if (this.inDataGrid(row, col)) {
      return this.dataProps
    }
    return this.outerProps
  }

  getSize(s: number): number {
    return (s - 2 * this.emptySpaceSize) / this.totalModuleCount
  }

  getEmptyOffset(x: number): number {
    if (this.emptySpaceSize === 0 || x < this.dataModuleOffset) {
      return 0
    }
    if (x < this.dataModuleBoundary) {
      return this.emptySpaceSize
    }
    return this.emptySpaceSize * 2
  }

  inDataGrid(row: number, col: number): boolean {
    if (row < this.dataModuleOffset || row >= this.dataModuleBoundary) {
      return false
    }
    if (col < this.dataModuleOffset || col >= this.dataModuleBoundary) {
      return false
    }
    return true
  }

  initialize(dataModuleCount: number): void {
    this.dataModuleCount = dataModuleCount

    this.dataModuleOffset = Math.ceil(
      ((dataModuleCount / this.dataGridPercentage) * 100 - dataModuleCount) / 2
    )
    this.totalModuleCount = this.dataModuleCount + 2 * this.dataModuleOffset
    this.dataModuleBoundary = this.totalModuleCount - this.dataModuleOffset

    const len = this.totalModuleCount
    this.modules = new Array(len)
    for (let row = 0; row < len; row++) {
      this.modules[row] = new Array(len)
      for (let col = 0; col < len; col++) {
        if (this.inDataGrid(row, col)) {
          this.modules[row][col] = {
            col: col,
            row: row,
            type: FcElement.UNSET,
            set: false,
            shape: this.dataProps.baseModuleShape,
            color: this.dataProps.colors[0]
          }
        } else {
          this.modules[row][col] = {
            col: col,
            row: row,
            type: FcElement.CONTAINER,
            set: this.doContainerPattern ? getRandomBoolean(2, this.seedGenerator) : false,
            shape: this.outerProps.baseModuleShape,
            color: this.outerProps.getColor()
          }
        }
      }
    }
  }

  isDataSet(row: number, col: number): boolean {
    if (row < 0 || this.dataModuleCount <= row || col < 0 || this.dataModuleCount <= col) {
      throw new Error(row + ',' + col)
    }
    row += this.dataModuleOffset
    col += this.dataModuleOffset
    return this.modules[row][col].set!
  }

  getDataModule(row: number, col: number): FcModule {
    row += this.dataModuleOffset
    col += this.dataModuleOffset
    return this.modules[row][col]
  }

  setData(
    row: number,
    col: number,
    isSet: boolean,
    type: FcElement,
    shape?: FcShape,
    byteIndex?: number,
    rsGroup?: number,
    rsAllowedErrors?: number,
    isPad?: boolean,
    bitIndex?: number,
    mask?: boolean
  ): void {
    if (row < 0 || this.dataModuleCount <= row || col < 0 || this.dataModuleCount <= col) {
      throw new Error(row + ',' + col)
    }
    row += this.dataModuleOffset
    col += this.dataModuleOffset
    const module = this.modules[row][col]
    module.set = isSet
    module.type = type
    module.byteIndex = byteIndex
    module.rsGroup = rsGroup
    module.rsAllowedErrors = rsAllowedErrors
    module.isPad = isPad
    module.bitIndex = bitIndex
    module.mask = mask
    if (byteIndex && byteIndex > this.highByte) {
      this.highByte = byteIndex
    }
    if (isSet) {
      if (shape) {
        module.shape = shape
      } else {
        module.shape = isPad ? this.dataProps.padModuleShape : this.dataProps.baseModuleShape
      }
      module.color = this.dataProps.getColor(isPad)
    }
  }

  getKeyVar(y: number, x: number, pos: number, isContainer: boolean): number {
    if (isContainer !== !this.inDataGrid(y, x)) {
      return 0
    }
    if (y < 0 || y >= this.totalModuleCount) {
      return 0
    }
    if (x < 0 || x >= this.totalModuleCount) {
      return 0
    }
    switch (pos) {
      case 3:
        if (x == this.dataModuleOffset - 1 || x == this.dataModuleBoundary - 1) {
          return 0
        }
        break
      case 2:
        if (y == this.dataModuleOffset - 1 || y == this.dataModuleBoundary - 1) {
          return 0
        }
        break
      case 1:
        if (x == this.dataModuleOffset || x == this.dataModuleBoundary) {
          return 0
        }
        break
      case 0:
        if (y == this.dataModuleOffset || y == this.dataModuleBoundary) {
          return 0
        }
        break
    }
    const module = this.modules[y][x]
    return module.set && !module.pipeErased ? 1 << pos : 0
  }

  applyPipes(): void {
    if (!this.dataProps.pipeImageUrls && !this.outerProps.pipeImageUrls) {
      return
    }
    for (let i = 0; i < this.totalModuleCount; i++) {
      for (let j = 0; j < this.totalModuleCount; j++) {
        const module = this.modules[i][j]
        if (!module.set) {
          continue
        }
        const isContainer = module.type == FcElement.CONTAINER
        const key =
          this.getKeyVar(i, j - 1, 3, isContainer) |
          this.getKeyVar(i - 1, j, 2, isContainer) |
          this.getKeyVar(i, j + 1, 1, isContainer) |
          this.getKeyVar(i + 1, j, 0, isContainer)
        module.pipeType = pipeMap.get(key)
      }
    }
  }

  applyTransformation(): void {
    if (!this.rotate) {
      return
    }
    const len = this.dataModuleCount + this.dataModuleOffset
    for (let i = this.dataModuleOffset; i < len; i++) {
      for (let j = i; j < len; j++) {
        const tmp = this.modules[i][j]
        this.modules[i][j] = this.modules[j][i]
        this.modules[j][i] = tmp
        this.modules[i][j].row = i
        this.modules[i][j].col = j
        this.modules[j][i].row = j
        this.modules[j][i].col = i
      }
    }
    for (let i = this.dataModuleOffset; i < len; i++) {
      for (let j = this.dataModuleOffset, k = len - 1; j < k; j++, k--) {
        const tmp = this.modules[i][j]
        this.modules[i][j] = this.modules[i][k]
        this.modules[i][k] = tmp
        this.modules[i][j].row = i
        this.modules[i][j].col = j
        this.modules[i][k].row = i
        this.modules[i][k].col = k
      }
    }
    const topLeft = this.positionElementAreas.get(FcPositionProperty.TOP_LEFT)!
    const botLeft = this.positionElementAreas.get(FcPositionProperty.BOTTOM_LEFT)!
    const topRight = this.positionElementAreas.get(FcPositionProperty.TOP_RIGHT)!
    const origY = botLeft.y
    botLeft.x = topLeft.x
    botLeft.y = topLeft.y
    topLeft.x = topRight.x
    topLeft.y = topRight.y
    topRight.y = origY
  }

  getPositionElementArea(position: FcPositionProperty): FcArea {
    return this.positionElementAreas.get(position)!
  }

  setPositionElementArea(position: FcPositionProperty, area: FcArea): void {
    area.x += this.dataModuleOffset
    area.y += this.dataModuleOffset
    this.positionElementAreas.set(position, area)
  }

  getPercentErasedOld(): number {
    const len = this.totalModuleCount
    const badBytes = new Set<number>()
    let erased = 0
    for (let row = 0; row < len; row++) {
      for (let col = 0; col < len; col++) {
        const module = this.modules[row][col]
        if (module.erased) {
          const byteIndex = module.byteIndex || 0
          if (!badBytes.has(byteIndex)) {
            badBytes.add(byteIndex)
            erased++
          }
        }
      }
    }
    return (erased / (this.highByte + 1)) * 100
  }

  getErasedRsGroups(): Map<number, RsGroup> {
    const len = this.totalModuleCount
    const rsGroups = new Map<number, RsGroup>()
    const rsGroupsList: RsGroup[] = []
    for (let row = 0; row < len; row++) {
      for (let col = 0; col < len; col++) {
        const module = this.modules[row][col]
        const rs = module.rsGroup === undefined ? -1 : module.rsGroup
        const rsAllowedErrors = module.rsAllowedErrors || -1
        if (rs === -1 || rsAllowedErrors == -1) {
          continue
        }
        const byteIndex = module.byteIndex
        if (byteIndex === undefined) {
          continue
        }
        let rsGroup = rsGroups.get(rs)
        if (!rsGroup) {
          rsGroup = {
            rsnum: rs,
            bytes: new Set<number>(),
            numBytes: 0,
            allowedErrors: rsAllowedErrors,
            numBadBytes: 0,
            badBytes: new Set<number>()
          }
          rsGroups.set(rs, rsGroup)
          rsGroupsList.push(rsGroup)
        }
        if (!rsGroup.bytes.has(byteIndex)) {
          rsGroup.bytes.add(byteIndex)
          rsGroup.numBytes++
        }
        if (module.erased) {
          if (!rsGroup.badBytes.has(byteIndex)) {
            rsGroup.badBytes.add(byteIndex)
            rsGroup.numBadBytes++
          }
        }
      }
    }
    return rsGroups
  }

  shuffle(array: number[]): number[] {
    let currentIndex = array.length,
      randomIndex

    // While there remain elements to shuffle...
    while (currentIndex != 0) {
      // Pick a remaining element...
      randomIndex = Math.floor(Math.random() * currentIndex)
      currentIndex--

      // And swap it with the current element.
      ;[array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]]
    }

    return array
  }
  getErasedData(): ErasedData {
    const rsGroups = this.getErasedRsGroups()
    const erasedData: ErasedData = {
      percentErased: 0,
      limitErasedInBlock: 0,
      overLimit: false,
      numErasedInBlock: 0
    }
    rsGroups.forEach((value, key, map) => {
      const percentErased = (value.numBadBytes / value.numBytes) * 100
      const overLimit = value.numBadBytes > value.allowedErrors
      if (
        (overLimit && !erasedData.overLimit) ||
        erasedData.limitErasedInBlock === 0 ||
        (percentErased > erasedData.percentErased && (!erasedData.overLimit || overLimit))
      ) {
        erasedData.overLimit = overLimit
        erasedData.percentErased = percentErased
        erasedData.numErasedInBlock = value.numBadBytes
        erasedData.limitErasedInBlock = value.allowedErrors
      }
    })
    return erasedData
  }
}

function getRandomBoolean(max: number, generator: rand.RandomSeed): boolean {
  const randomInt = generator.range(max)
  return randomInt === 0 ? false : true
}
